import * as Sentry from '@sentry/react';

// Category must follow "domain.action" pattern (e.g., 'chart.meta', 'data.loading')
type SentryCategory = `${string}.${string}`;

type SentryLevel = 'debug' | 'info' | 'warning' | 'error' | 'fatal';

type SentryScopeOptions = {
    context?: Record<string, Record<string, unknown>>;
    tags?: Record<string, string>;
};

type SentryBreadcrumbOptions = {
    category: SentryCategory;
    message: string;
    level?: SentryLevel;
    data?: Record<string, unknown>;
};

type SentryCaptureMessageOptions = SentryScopeOptions & {
    message: string;
    level?: SentryLevel;
};

type SentryCaptureExceptionOptions = SentryScopeOptions & {
    error: unknown;
};

function applyScopeOptions(
    scope: Sentry.Scope,
    { tags, context }: SentryScopeOptions,
): void {
    if (tags) {
        Object.entries(tags).forEach(([key, value]) => {
            scope.setTag(key, value);
        });
    }
    if (context) {
        Object.entries(context).forEach(([key, value]) => {
            scope.setContext(key, value);
        });
    }
}

// Breadcrumbs are not errors - they provide context that appears in Sentry when a real error occurs later
function sentryAddBreadcrumb({
    category,
    message,
    level = 'info',
    data,
}: SentryBreadcrumbOptions): void {
    Sentry.addBreadcrumb({
        category,
        message,
        level,
        data,
    });
}

function sentryCaptureMessage({
    message,
    level = 'info',
    context,
    tags,
}: SentryCaptureMessageOptions): void {
    Sentry.withScope((scope) => {
        applyScopeOptions(scope, { tags, context });
        Sentry.captureMessage(message, level);
    });
}

function sentryCaptureException({
    error,
    context,
    tags,
}: SentryCaptureExceptionOptions): void {
    Sentry.withScope((scope) => {
        applyScopeOptions(scope, { tags, context });
        Sentry.captureException(error);
    });
}

// Context persists for the session - use sparingly
function sentrySetContext(name: string, context: Record<string, unknown>): void {
    Sentry.setContext(name, context);
}

// Tags are indexed - keep values low-cardinality (no UUIDs or timestamps)
function sentrySetTag(key: string, value: string): void {
    Sentry.setTag(key, value);
}

type SentryLogAttributes = Record<string, unknown>;

// Standalone structured logs — searchable in Sentry Logs UI independent of errors.
// Unlike breadcrumbs (which only appear when an error fires), these are always visible.
// Requires enableLogs: true in Sentry.init().
const sentryLog = {
    trace: (message: string, attributes?: SentryLogAttributes) =>
        Sentry.logger.trace(message, attributes),
    debug: (message: string, attributes?: SentryLogAttributes) =>
        Sentry.logger.debug(message, attributes),
    info: (message: string, attributes?: SentryLogAttributes) =>
        Sentry.logger.info(message, attributes),
    warn: (message: string, attributes?: SentryLogAttributes) =>
        Sentry.logger.warn(message, attributes),
    error: (message: string, attributes?: SentryLogAttributes) =>
        Sentry.logger.error(message, attributes),
    fatal: (message: string, attributes?: SentryLogAttributes) =>
        Sentry.logger.fatal(message, attributes),
    fmt: Sentry.logger.fmt,
} as const;

export type {
    SentryBreadcrumbOptions,
    SentryCaptureExceptionOptions,
    SentryCaptureMessageOptions,
    SentryCategory,
    SentryLevel,
    SentryLogAttributes,
};
export {
    sentryAddBreadcrumb,
    sentryCaptureException,
    sentryCaptureMessage,
    sentryLog,
    sentrySetContext,
    sentrySetTag,
};
